<template>
    <vx-modal v-model:visible="visible" size="lg" padding="standard" :preventClosing="submitting">
        <template v-slot:header>Refresh carrier terms</template>
        <template v-slot:subheader>{{ props.campaignId }}</template>

        <div class="flex flex-col gap-4 text-sm text-slate-800">
            <div>
                A campaign keeps the carrier terms it was registered with until it's resubmitted. Only carriers offering
                better terms can be picked.
            </div>
            <page-loading v-if="loading"></page-loading>
            <template v-else-if="terms">
                <vx-alert
                    v-if="terms.qualification_error"
                    color="info"
                    :closeable="false"
                    data-test="qualification-error"
                >
                    TCR has no new terms for this brand yet: {{ terms.qualification_error }}
                </vx-alert>
                <vx-alert
                    v-else-if="!terms.eligible_mno_ids.length"
                    color="slate"
                    :closeable="false"
                    data-test="none-better"
                >
                    No carrier offers this campaign better terms right now.
                </vx-alert>
                <vx-table class="xl:grid-cols-5" rowBreakpoint="xl">
                    <vx-table-row isHeader>
                        <div>Carrier</div>
                        <div>Excluded</div>
                        <div>Qualify</div>
                        <div>Current</div>
                        <div>New</div>
                    </vx-table-row>
                    <vx-table-row
                        v-for="carrier in terms.carriers"
                        :key="carrier.mno_id"
                        :data-test="`carrier-${carrier.mno_id}`"
                    >
                        <vx-table-row-title>
                            <vx-checkbox
                                :modelValue="selected.includes(carrier.mno_id)"
                                @update:modelValue="toggle(carrier.mno_id, $event)"
                                :label="
                                    mnoLabel(carrier.mno_id, carrier.current?.mno ?? carrier.available?.mno ?? null)
                                "
                                :disabled="!terms.eligible_mno_ids.includes(carrier.mno_id)"
                                :data-test="`select-${carrier.mno_id}`"
                            ></vx-checkbox>
                        </vx-table-row-title>
                        <vx-table-row-item>
                            <template v-slot:label> Excluded: </template>
                            {{ yesNo(carrier.excluded) }}
                        </vx-table-row-item>
                        <vx-table-row-item>
                            <template v-slot:label> Qualify: </template>
                            {{ yesNo(carrier.available?.qualify ?? null) }}
                        </vx-table-row-item>
                        <vx-table-row-item data-test="current-terms">
                            <template v-slot:label> Current: </template>
                            {{
                                null === carrier.current
                                    ? 'Not registered'
                                    : carrierTermsLabel(carrier.mno_id, carrier.current)
                            }}
                        </vx-table-row-item>
                        <vx-table-row-item data-test="new-terms">
                            <template v-slot:label> New: </template>
                            {{ carrierTermsLabel(carrier.mno_id, carrier.available) }}
                        </vx-table-row-item>
                    </vx-table-row>
                </vx-table>
                <div class="text-slate-500">AT&T: class / TPM / MMS TPM. T-Mobile: tier / daily cap.</div>
            </template>
        </div>

        <template v-slot:footer>
            <div class="flex flex-col-reverse gap-4 lg:flex-row">
                <vx-button
                    @click="!submitting && (visible = false)"
                    data-test="cancel-refresh"
                    type="button"
                    class="grow lg:w-20 lg:grow-0"
                    size="lg"
                    color="muted"
                >
                    Cancel
                </vx-button>
                <vx-button
                    @click="confirm()"
                    data-test="confirm-refresh"
                    :disabled="!selected.length"
                    :loading="submitting"
                    type="button"
                    class="grow"
                    size="lg"
                    color="primary"
                >
                    Resubmit with new terms
                </vx-button>
            </div>
        </template>
    </vx-modal>
</template>

<script lang="ts" setup>
import {
    VxAlert,
    VxButton,
    VxCheckbox,
    VxModal,
    VxTable,
    VxTableRow,
    VxTableRowItem,
    VxTableRowTitle,
} from '@voxie/frontend-components';
import { isCancel } from 'axios';
import { ref, watch } from 'vue';
import PageLoading from '~/components/general/PageLoading.vue';
import {
    carrierTermsLabel,
    mnoLabel,
    yesNo,
} from '~/components/kiosk/kiosk-ten-dlc/registration/tenDlcRegistrationLabels';
import { useToasted } from '~/composables/useToasted';
import { adminTenDlcRegistrationClient } from '~/services';
import type { TcrCampaignCarrierTerms } from '~/services/adminTenDlcRegistrationClient.types';
import { catchRequestError } from '~/utils/request';

const props = defineProps<{ campaignId: string }>();
const emit = defineEmits<{ refreshed: [complete: boolean] }>();
const visible = defineModel<boolean>('visible', { default: false });

const toasted = useToasted();

const terms = ref<TcrCampaignCarrierTerms | null>(null);
const selected = ref<string[]>([]);
const loading = ref(false);
const submitting = ref(false);

const termsCancelKey = Symbol('kiosk-tcr-campaign-terms-refresh');

const load = () => {
    loading.value = true;

    adminTenDlcRegistrationClient
        .getCampaignCarrierTerms(props.campaignId, { cancelKey: termsCancelKey })
        .then((response) => {
            terms.value = response.data;
            selected.value = [...response.data.eligible_mno_ids];
            loading.value = false;
        })
        .catch((e) => {
            if (isCancel(e)) {
                return;
            }

            loading.value = false;
            toasted.error(catchRequestError(e), { sticky: true });
            visible.value = false;
        });
};

const toggle = (mnoId: string, checked: boolean) => {
    selected.value = checked ? [...selected.value, mnoId] : selected.value.filter((id) => id !== mnoId);
};

watch(
    visible,
    (open) => {
        if (!open) {
            return;
        }

        terms.value = null;
        selected.value = [];
        load();
    },
    { immediate: true }
);

const confirm = async () => {
    if (submitting.value || null === terms.value || !selected.value.length) {
        return;
    }

    const mnoIds = selected.value;

    submitting.value = true;

    try {
        await adminTenDlcRegistrationClient.refreshCampaignCarrierTerms(props.campaignId, { mno_ids: mnoIds });

        emit('refreshed', mnoIds.length === terms.value.eligible_mno_ids.length);
        visible.value = false;
    } catch (e) {
        toasted.error(catchRequestError(e), { sticky: true });
    } finally {
        submitting.value = false;
    }
};
</script>
